import CloseIcon from "@assets/images/CloseModal.svg";
import SendMailIcon from "@assets/images/send-mail.svg";
import SendMailOkIcon from "@assets/images/sent-mail-success.svg";
import {
  BackdropProps,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid
} from "@material-ui/core";
import MyTypography from "@saleor/components/MyTypography";
import { SingleTextField } from "@saleor/components/SingleTextField";
import TextEditor from "@saleor/components/TextEditor";
import TextInputDisable from "@saleor/components/TextInputDisable";
import {
  useGetOrderEmailToAndCcForNotRefQuery,
  useSendEmailFromChangeOrderMutation
} from "@saleor/graphql";
import { useFormik } from "formik";
import moment from "moment/moment";
import React, { useState } from "react";
import SVG from "react-inlinesvg";
import { useIntl } from "react-intl";
import * as Yup from "yup";

import useStyles from "./styles";

interface SendMailProps {
  open: boolean;
  soldToCode: string[];
  soldToName: string;
  onClose: () => void;
  saleOrg?: string;
  soNo?: string;
}

interface FormValues {
  to: string;
  cc: string;
  subject: string;
  content: string;
  soNo: string;
}

const ModalSendMailNotRef: React.FC<SendMailProps> = props => {
  const { open, onClose, soldToCode, soldToName, saleOrg, soNo } = props;
  const [openSuccess, setOpenSuccess] = useState<boolean>(false);
  const [openMailConfirm, setOpenMailConfirm] = useState<boolean>(false);
  const [submittedData, setSubmittedData] = useState<FormValues>(null);
  const classes = useStyles();
  const intl = useIntl();

  const SendMailSchema = Yup.object().shape({
    to: Yup.string().required("Field To is required"),
    cc: Yup.string().required("Field Cc is required"),
    content: Yup.string().required("Field Content is required")
  });

  const { data: dataMail, loading } = useGetOrderEmailToAndCcForNotRefQuery({
    variables: {
      soldToCode,
      saleOrg: [saleOrg],
      soNo
    },
    skip: soldToCode.length === 0 || !open
  });

  const formik = useFormik<FormValues>({
    initialValues: {
      to: dataMail?.getOrderEmailToAndCcForNotRef?.to,
      cc: dataMail?.getOrderEmailToAndCcForNotRef?.cc,
      subject: `Order Confirmation ${soldToName} ${moment().format(
        "DD/MM/YYYY"
      )}`,
      content:
        "<p>Dear Sir,</p>" +
        "<p>Please kindly find Order Confirmation as per file attached.</p>" +
        "<p></p>" +
        "<p>Best Regards,</p>" +
        "<p>Customer Services</p>" +
        "<p>SCGP</p>",
      soNo
    },
    enableReinitialize: true,
    onSubmit: data => {
      setSubmittedData(data);
      onClose();
      setOpenMailConfirm(true);
    },
    validationSchema: SendMailSchema
  });

  const [
    sendMailOrder,
    { loading: sendMailOrderLoading }
  ] = useSendEmailFromChangeOrderMutation({
    onCompleted: data => {
      if (data?.sendEmailFromChangeOrder?.errors.length === 0) {
        setOpenMailConfirm(false);
        setSubmittedData(null);
        setOpenSuccess(true);
        onClose();
      }
    }
  });

  const handleSubmit = () => {
    formik.submitForm();
  };

  const handleConfirm = () => {
    if (submittedData) {
      sendMailOrder({ variables: submittedData });
    }
  };

  const isLoading = loading || sendMailOrderLoading;

  return (
    <>
      <Dialog
        data-testid="sendMailNotRef-modal"
        aria-busy={isLoading}
        className={classes.root}
        open={open}
        onClose={onClose}
        BackdropProps={
          {
            "data-testid": "sendMailNotRef-modal-backdrop"
          } as Partial<BackdropProps>
        }
      >
        <DialogTitle className={classes.dialogTitle}>
          <div className={classes.flexCenter}>
            <SVG src={SendMailIcon} className={classes.icon} />
            <MyTypography variant={"h5"}>
              {intl.formatMessage({ defaultMessage: "Send Email" })}
            </MyTypography>
          </div>
          <SVG
            onClick={onClose}
            width={24}
            height={24}
            src={CloseIcon}
            className={classes.closeIcon}
          />
        </DialogTitle>
        <DialogContent>
          <Grid
            spacing={3}
            container
            alignItems={"center"}
            className={classes.gridBox}
          >
            <Grid xs={12} sm={2} item>
              <MyTypography variant="body3">
                {intl.formatMessage({ defaultMessage: "To" })}
                <span className={classes.required}>*</span>
              </MyTypography>
            </Grid>
            <Grid xs={12} sm={10} item>
              <SingleTextField
                name={"to"}
                value={formik.values.to}
                onChange={formik.handleChange}
                error={!!formik.errors.to}
              />
            </Grid>
            <Grid xs={12} sm={2} item>
              <MyTypography variant="body3">
                {intl.formatMessage({ defaultMessage: "Cc" })}
                <span className={classes.required}>*</span>
              </MyTypography>
            </Grid>
            <Grid xs={12} sm={10} item>
              <SingleTextField
                name={"cc"}
                value={formik.values.cc}
                onChange={formik.handleChange}
                error={!!formik.errors.cc}
              />
            </Grid>
            <Grid xs={12} sm={2} item>
              <MyTypography variant="body3">
                {intl.formatMessage({ defaultMessage: "Subject" })}:
              </MyTypography>
            </Grid>
            <Grid xs={12} sm={10} item>
              <TextInputDisable id="subject" value={formik.values.subject} />
            </Grid>
            <Grid sm={12} item>
              <div className={classes.contentBlock}>
                <MyTypography variant="body3" className={classes.contentTitle}>
                  {intl.formatMessage({ defaultMessage: "Message" })}
                  <span className={classes.required}>*</span>
                </MyTypography>
                <div className={classes.contentEditor}>
                  <TextEditor
                    name={"content"}
                    value={formik.values.content}
                    onChange={formik.handleChange}
                  />
                </div>
              </div>
            </Grid>
            <Grid sm={12} item className={classes.flexEnd}>
              <Button
                id="sendButton"
                disabled={loading}
                type={"submit"}
                color="secondary"
                variant="contained"
                onClick={handleSubmit}
              >
                <MyTypography variant="body1">
                  {intl.formatMessage({ defaultMessage: "Send" })}
                </MyTypography>
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      <Dialog open={openMailConfirm}>
        <DialogContent className={classes.boxConfirm}>
          <div className={classes.flexCenter}>
            <span className={classes.iconMail}>
              <SVG src={SendMailIcon} className={classes.icon} />
            </span>
            <MyTypography variant={"h6"}>
              {intl.formatMessage({ defaultMessage: "ยืนยันส่งอีเมล ?" })}
            </MyTypography>
          </div>
          <MyTypography variant={"body2"} className={classes.boxConfirmDesc}>
            {intl.formatMessage({
              defaultMessage:
                "กรุณากด “ยืนยัน” เพื่อดำเนินการต่อ หรือกด “กลับไป” เพื่อปิดหน้าต่างนี้"
            })}
          </MyTypography>
        </DialogContent>
        <DialogActions className={classes.actionWrap}>
          <Button
            id="backButton"
            color="secondary"
            variant="outlined"
            onClick={() => setOpenMailConfirm(false)}
          >
            <MyTypography variant="body1">
              {intl.formatMessage({ defaultMessage: "กลับไป" })}
            </MyTypography>
          </Button>
          <Button
            id="confirmButton"
            disabled={sendMailOrderLoading}
            color="secondary"
            variant="contained"
            onClick={handleConfirm}
          >
            <MyTypography variant="body1">
              {intl.formatMessage({ defaultMessage: "ยืนยัน" })}
            </MyTypography>
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openSuccess}>
        <DialogContent className={classes.boxSuccess}>
          <div className={classes.flexCenter}>
            <SVG src={SendMailOkIcon} className={classes.icon} />
            <MyTypography variant={"h6"}>
              {intl.formatMessage({ defaultMessage: "ส่งอีเมล สำเร็จ" })}
            </MyTypography>
          </div>
          <MyTypography variant={"body2"}>
            {intl.formatMessage({
              defaultMessage: "ระบบ eOrdering ได้ส่งอีเมลของคุณเรียบร้อยแล้ว"
            })}
          </MyTypography>
          <Button
            id="closeButton"
            fullWidth
            color="secondary"
            variant="contained"
            onClick={() => setOpenSuccess(false)}
          >
            <MyTypography variant="body1">
              {intl.formatMessage({ defaultMessage: "ปิดหน้าต่าง" })}
            </MyTypography>
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ModalSendMailNotRef;
